import { useQuery } from "@apollo/client";
import { StringLike } from "@visx/scale";
import { format } from "date-fns/esm";
import { useMemo } from "react";

import { Spinner } from "components/Spinner";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import { AspectGranularity, AspectInput, AspectOutput, AspectType } from "types/generated";

import { GET_ASPECT } from "../gql";
import { TimeFilter } from "../types";
import {
  adaptMetricToChart,
  adaptPercentilesToChart,
  adaptTimeFilterForApi,
  getChartItems,
} from "../utils";
import { GroupByKey } from "./constants";
import { ChartColor } from "./types";
import { getLegendTitleByGroup } from "./utils";
import UsageChartView from "./View";

type UsageChartProps = {
  aspectType: AspectType;
  timeFilter: TimeFilter;
  groupBy?: GroupByKey;
  tooltipHeader?: string;
  leftNumTicks?: number;
  showItemsInTooltip?: boolean;
  leftAxisLabel?: string;
  legendTitle?: string;
  showPercentiles?: boolean;
  colorMapper: (item: string) => ChartColor;
};

const formatXAxisLabel = (value: StringLike) => format(Number(value) * 1000, "EEE d MMM");

const UsageChart = ({
  aspectType,
  timeFilter,
  groupBy,
  tooltipHeader,
  leftNumTicks,
  colorMapper,
  leftAxisLabel,
  legendTitle,
  showItemsInTooltip = true,
  showPercentiles = true,
}: UsageChartProps) => {
  const { data, loading } = useQuery<{ usageAspect: AspectOutput }, { input: AspectInput }>(
    GET_ASPECT,
    {
      variables: {
        input: {
          type: aspectType,
          groupBy: groupBy || null,
          timeFilter: adaptTimeFilterForApi(timeFilter),
          granularity: AspectGranularity.Day,
        },
      },
    }
  );

  const keys = useMemo(() => getChartItems(data?.usageAspect?.data || []), [data?.usageAspect]);

  if (loading) {
    return (
      <Box grow="1" justify="center" align="center">
        <Icon src={Spinner} size="x-large" />
      </Box>
    );
  }

  if (!data?.usageAspect) return null; // TODO: handle error more gracefully

  return (
    <UsageChartView
      data={adaptMetricToChart(data.usageAspect, keys)}
      items={keys}
      colorMapper={colorMapper}
      formatXAxisLabel={formatXAxisLabel}
      tooltipHeader={tooltipHeader}
      legendTitle={legendTitle || getLegendTitleByGroup(groupBy)}
      leftNumTicks={leftNumTicks}
      showItemsInTooltip={showItemsInTooltip && !!groupBy}
      leftAxisLabel={leftAxisLabel}
      percentiles={
        showPercentiles ? adaptPercentilesToChart(data.usageAspect.summary?.percentiles) : undefined
      }
    />
  );
};

export default UsageChart;
